import xss from '@/utils/xssFilter'
import filter from '@/utils/filter'
import { getYoutubeId, getBase64URL } from '@/utils'
import { useSystemStore } from '@/stores/system'

const threadXssWhiteList = {
  b: [],
  br: [],
  hr: [],
  sup: [],
  i: ["style"],
  u: ["style"],
  em: ["style"],
  ul: ["style"],
  ol: ["style"],
  li: ["style"],
  p: ["style", "class"],
  div: ["style", "class"],
  span: ["style", "class"],
  strong: ["style", "class"],
  a: ['data-type', 'data-value'],
  video: ['data-type', 'data-value'],
  img: ['data-type', 'data-value', 'data-reduce', 'src']
}

const editorXssWhiteList = {
  sup: [],
  hr: [],
  p: ["style", "class"],
  i: ["style", "class"],
  b: ["style", "class"],
  u: ["style", "class"],
  em: ["style", "class"],
  br: ["style", "class"],
  ul: ["style", "class"],
  ol: ["style", "class"],
  li: ["style", "class"],
  span: ["style", "class"],
  strong: ["style", "class"],
  a: ["style", "class", 'data-type', 'data-value'],
  div: ["style", "class", "contenteditable", "data-hiddenline"],
  img: ["style", "class", 'data-type', 'data-value', 'data-w', 'data-h', 'data-reduce', 'src'],
  video: ["style", "class", "controls", "muted", "controlslist", "poster", 'data-type', 'data-value', 'data-src']
}

/**
 * @description 生成对应正则，并匹配相应值
 * @param {Object}  
 *    @param {*} param0.tag 标签名
 *    @param {*} param0.key  匹配标签属性
 *    @param {*} param0.regString  匹配对象
 *    @param {*} param0.reg 正则表达式
 */
function getRegex ({ tag, key, regString, reg }) {
  const regex = reg || new RegExp(`<${tag}[^>]*\\s${key}="([^"]*)"`, 'gi')
  let matchValue = regex.exec(regString)
  return matchValue?.[1]
}

// 格式化视频标签
const formatVideo = (content, attachments, viewType) => {
  const videoList = content.match(/<video.*?<\/video>/gi) || []
  let formatVideoContent = JSON.parse(JSON.stringify(content))
  videoList.forEach(video => {
    const dataType = getRegex({
      tag: 'video', 
      key: 'data-type', 
      regString: video
    })
    const dataValue = getRegex({
      tag: 'video', 
      key: 'data-value', 
      regString: video
    })
    let src = ''
    let targetAttachments = {}
    let videoRatioType = 'transverseVideo'
    if (dataType === 'file') {
      const systemStore = useSystemStore()
      src = attachments.find(a => a.id === dataValue)?.url
      targetAttachments = attachments.find(a => a.id === dataValue) || {}
      // 视频横竖屏判断（仅移动端支持）
      const ratio = systemStore.isWap && (targetAttachments.width / targetAttachments.height < 0.75)  ? '3/4' : '16/9'
      if(systemStore.isWap && targetAttachments.width){
        videoRatioType = ratio === '3/4'  ? 'vertical-video' : 'transverseVideo'
      }
    }
    if (dataType === 'youtube') {
      src = dataValue
    }
    // 处理文件定向到私有桶，用于未发布成功的富文本
    if (src?.includes('/ugc/frontend/attachment/view')) {
      if (import.meta.env.VITE_ENV === 'DEV') {
        try {
          let url = new URL(src)
          src = `${location.origin}${src.includes('proxy') ? '' : '/proxy'}${url.pathname}${url.search}` 
        } catch {}
      }
      // 私有桶需要拼接app标识用于鉴权
      src = src ? `${src}${src.includes('appCode') ? '' : `${src.includes('?') ? '&' : '?'}appCode=community&t=${Date.now()}`}` : ''
    }
    // 编辑器内使用iframe渲染youtube视频
    if ((viewType === 'editor') && dataType === 'youtube') {
      const youtubeId = getYoutubeId(src)
      formatVideoContent = formatVideoContent.replace(video, `<p class="media-preview" data-media-value="${dataValue}"><iframe data-value="${dataValue}" data-type="${dataType}" src="https://www.youtube.com/embed/${youtubeId}?enablejsapi=1&origin=${location.origin}" frameborder="0"></iframe></p>`)
      return
    }
    formatVideoContent = formatVideoContent.replace(video, `<p class="media-preview ${videoRatioType}" data-media-value="${dataValue}"><video width="${targetAttachments.width}" height="${targetAttachments.height}" data-type="${dataType}" data-value="${dataValue}" data-src="${src}" src="${src}" controls></video></p>`)
  })
  return formatVideoContent
}

// 格式化图片标签
const formatImg = (content, attachments, imgMaxWidth, viewType, cloudTransFer) => {
  const systemStore = useSystemStore()
  let formatImgContent = JSON.parse(JSON.stringify(content))
  // 图片合集
  const imgList = content.match(/<img [^>]*>/gi) || []
  // 图片处理
  imgList.forEach(img => {
    // 图片类型
    const dataType = getRegex({
      tag: 'img', 
      key: 'data-type', 
      regString: img
    })
    // 图片id/链接
    const dataValue = getRegex({
      tag: 'img', 
      key: 'data-value', 
      regString: img
    })
    // 是否缩放
    const dataReduce = getRegex({
      tag: 'img', 
      key: 'data-reduce', 
      regString: img
    })
    const isReduce = dataReduce === 'true'
    // 图片替换元素
    let replaceImg = ''
    // 图片附件信息
    let { width: originWidth, height: originHeight, url: src, oliveInfo } = attachments.find(a => a.id === dataValue) || {}
    if (!src) return
    // 图片占位宽高处理,携带缩放标识的按80%比例展示
    let width = `${isReduce ? imgMaxWidth * 0.8 : imgMaxWidth}`
    let height = `${isReduce ? ((originHeight / originWidth) * imgMaxWidth) * 0.8 : (originHeight / originWidth) * imgMaxWidth}`

    // 处理文件定向到私有桶，用于未发布成功的富文本
    if (src?.includes('/ugc/frontend/attachment/view')) {
      if (import.meta.env.VITE_ENV === 'DEV') {
        try {
          let url = new URL(src)
          src = `${location.origin}${src.includes('proxy') ? '' : '/proxy'}${url.pathname}${url.search}` 
        } catch {}
      }
      // 私有桶需要拼接app标识用于鉴权
      src = src ? `${src}${src.includes('appCode') ? '' : `${src.includes('?') ? '&' : '?'}appCode=community&t=${Date.now()}`}` : ''
    }
    // 是否启用视频云转码
    if (cloudTransFer) {
      let formatHeight = null
      let formatWidth = null
      if (originHeight >= 16383) {
        formatHeight = originWidth >= 1400 ? (16000 / 2) : 16383
      } else {
        formatWidth = originWidth >= (width * 2) ? (width * 2) : originWidth
      }
      src = filter.formatImg(src, formatWidth || '', formatHeight || '')
    }

    // 图处理; oliveInfo:实况图处理
    if(viewType === 'thread'){
      if(oliveInfo){
        replaceImg = `<div style="width:${width}px;height:${height}px" data-type="olive"><img src="/photo/live.png" data-type="live_self" /><img data-type="${dataType}" data-value="${dataValue}" data-src="${src}" data-reduce="${isReduce}" width="${width}" height="${height}" /></div>` 
      }else{
        replaceImg = `<img data-type="${dataType}" data-value="${dataValue}" data-src="${src}" data-reduce="${isReduce}" width="${width}" height="${height}" />`
      }
    }else if(viewType === 'commentListImg'){
      // 帖子列表下的评论
      // 高/宽 = 1 正常显示；
      // 高/宽 > 1 竖图;  PC: 宽度固定195px 最大高度347px; M：宽度固定148px 最大高度 263px
      // 高/宽 < 1 横图;  宽度固定195px 最大高度146px，宽度大于高度的1.5倍时截取中间部分，否则高度自适应
      if(height / width < 1){   // 横图
        if(width / height >= 1.5 || height > 146){
          height  = 146
        }
      }else{ // 竖图
        if(systemStore.isWap){
          if(height / width === 1){
            height = 148
          }else{
            height = height > 329 ? 329 : height  
          }
          width = 148
        }else{
          height = height > 433 ? 433 : height
        }
      }
      if (oliveInfo) {
        replaceImg = `<div style="width:${width}px;height:${height}px;" data-type="olive"><img src="/photo/live.png" data-type="live_self" /><img data-type="${dataType}" data-value="${dataValue}" data-src="${src}" data-reduce="${isReduce}" width="${width}" height="${height}" style="object-fit: cover" /></div>` 
      }else{
        replaceImg = `<img data-type="${dataType}" data-value="${dataValue}" data-src="${src}" data-reduce="${isReduce}" width="${width}" height="${height}" style="object-fit: cover"/>`
      }
    }else{
      if(oliveInfo){
        replaceImg = `<p class="media-preview" data-media-value=${dataValue} data-reduce="${isReduce}"><img src="/photo/live.png"  data-type="live_self"/><img data-type="${dataType}" data-value="${dataValue}" data-src="${src}" data-reduce="${isReduce}" width="${width}" height="${height}"/><img class="status reduce" src="/photo/reduce.png" /></p>`
      }else{
        replaceImg = `<p class="media-preview" data-media-value="${dataValue}" data-reduce="${isReduce}"><img data-type="${dataType}" data-value="${dataValue}" data-src="${src}" data-reduce="${isReduce}" width="${width}" height="${height}"/><img class="status reduce" src="/photo/reduce.png" /></p>`
      }
    }
        
    // 实况图处理
    // if (oliveInfo) {
    //   replaceImg = viewType === 'thread' ? 
    //     `<div style="width:${width}px;height:${height}px" data-type="olive"><img src="/photo/live.png" data-type="live_self" /><img data-type="${dataType}" data-value="${dataValue}" data-src="${src}" data-reduce="${isReduce}" width="${width}" height="${height}" /></div>` 
    //   :
    //     `<p class="media-preview" data-media-value=${dataValue} data-reduce="${isReduce}"><img src="/photo/live.png"  data-type="live_self"/><img data-type="${dataType}" data-value="${dataValue}" data-src="${src}" data-reduce="${isReduce}" width="${width}" height="${height}"/><img class="status reduce" src="/photo/reduce.png" /></p>`
    // }

    // 自定义表情
    if (dataType === 'sticker_self') {
      const stickerSrc = getRegex({
        tag: 'img', 
        key: 'src', 
        regString: img
      })
      replaceImg = `<img data-type="${dataType}" data-value="${dataValue}" src="${stickerSrc}"/>`
    }
    formatImgContent = formatImgContent.replace(img, replaceImg)
  })
  return formatImgContent
}
// 处理@人/超链接
const formatAtTag = (content) => {
  let formatAtContent = JSON.parse(JSON.stringify(content))
  const atList = content.match(/<a.*?<\/a>/gi) || []
  atList.forEach(a => {
    const dataType = getRegex({
      tag: 'a', 
      key: 'data-type', 
      regString: a
    })
    const dataValue = getRegex({
      tag: 'a', 
      key: 'data-value', 
      regString: a
    })
    const tagText = getRegex({
      reg: /<a[^>]*>(.*?)<\/a>/gi,
      regString: a
    })
    let href = ''
    if (dataType === 'link') {
      href = dataValue || ''
    }
    if (dataType === '@') {
      href = `${location.origin}/user/${dataValue}`
    }
    const target = href.includes(location.origin) ? '_self' : '_blank'
    formatAtContent = formatAtContent.replace(a, `
      <a data-type="${dataType}" data-value="${dataValue}" href="${href}" target="${target}">${tagText}</a>
    `)
  })
  return formatAtContent
}
// 短文媒体标签处理
const formatMomentImg = (content, attachments, ext, imgMaxWidth, cloudTransFer) => {
  const systemStore = useSystemStore()
  let formatMomentContent = JSON.parse(JSON.stringify(content))
  attachments.forEach(i => {
    let insertTag = ''
    if (i.type === 2) {
      insertTag = `<video data-type="file" data-value="${i.id}" data-src="${i.url}"></video>`
    } else {
      if (systemStore.isWeb) {
        let { width, height, url } = i
        height =  `${(height / width) * imgMaxWidth}px`
        if (cloudTransFer) {
          // const formatHeight = i.height >= 16383 ? (16000 / 2) : i.height / 2
          // url = filter.formatImg(url, '', formatHeight)

          let formatHeight = null
          let formatWidth = null
          if (i.height >= 16383) {
            formatHeight = i.width >= 1400 ? (16000 / 2) : 16383
          } else {
            formatWidth = (imgMaxWidth * 2)
            formatWidth = i.width >= (imgMaxWidth * 2) ? (imgMaxWidth * 2) : i.width
          }
          url = filter.formatImg(url, formatWidth || '', formatHeight || '')
        }
        if(i.oliveInfo){
          insertTag = `<div data-type="olive"><img src="/photo/live.png" data-type="live_self" /><img data-type="file" data-value="${i.id}" data-src="${url}" width="${imgMaxWidth}px" height="${height}" /></div>`
        }else{
          insertTag = `<img data-type="file" data-value="${i.id}" data-src="${url}" width="${imgMaxWidth}px" height="${height}"/>`
        }
      }
    }
    formatMomentContent = formatMomentContent.concat(insertTag)
  })
  // 短文pc端youtube链接处理
  if (ext.length) {
    ext.forEach(e => {
      const youtubeTag = `<video data-type="youtube" data-value="${e.url}" data-src="${e.url}"></video>`
      formatMomentContent = formatMomentContent.concat(youtubeTag)
    })
  }
  return formatMomentContent
}

// 移除所有非表情包图片
function removeImg (content) {
  let contentBeforeRemoveImg = JSON.parse(JSON.stringify(content))
  const regex = new RegExp(`<([a-z]+)([^>]*\\bolive\\b[^>]*)>(.*?)</\\1>`, 'gi');
  const oliveDom = contentBeforeRemoveImg.match(regex) || []
  oliveDom.forEach(live => {
    // 图片id/链接
    const dataValue = getRegex({
      tag: 'img', 
      key: 'data-value', 
      regString: live
    })
    contentBeforeRemoveImg = contentBeforeRemoveImg.replace(live, '')
  })
  const imgList = content.match(/<img [^>]*>/gi) || []
  imgList.forEach(img => {
    const dataType = getRegex({
      tag: 'img', 
      key: 'data-type', 
      regString: img
    })
    // 图片id/链接
    const dataValue = getRegex({
      tag: 'img', 
      key: 'data-value', 
      regString: img
    })
    if (dataType !== 'sticker_self') {
      contentBeforeRemoveImg = contentBeforeRemoveImg.replace(img, '')
    }
  })
  return contentBeforeRemoveImg
}

// 引用内的图片替换为文本
function replaceQuoteImg (content) {
  let contentBeforeRemoveImg = JSON.parse(JSON.stringify(content))
  const regex = new RegExp(`<([a-z]+)([^>]*\\bolive\\b[^>]*)>(.*?)</\\1>`, 'gi');
  const oliveDom = contentBeforeRemoveImg.match(regex) || []
  oliveDom.forEach(live => {
    const dataValue = getRegex({
      tag: 'img', 
      key: 'data-value', 
      regString: live
    })
    contentBeforeRemoveImg = contentBeforeRemoveImg.replace(live, `<div data-value="${dataValue}">[Picture]<div>`)
  })
  const imgList = content.match(/<img [^>]*>/gi) || []
  imgList.forEach(img => {
    const dataType = getRegex({
      tag: 'img', 
      key: 'data-type', 
      regString: img
    })
    const dataValue = getRegex({
      tag: 'img', 
      key: 'data-value', 
      regString: img
    })
    if (dataType !== 'sticker_self') {
      contentBeforeRemoveImg = contentBeforeRemoveImg.replace(img, `<div data-imgId="${dataValue}">[Picture]</div>`)
    }
  })
  return contentBeforeRemoveImg
}


/** 
 * @description: 富文本展示处理
 * @param {Object} threadDetail 帖子信息
 *   @param {Number} type 帖子类型
 *   @param {String} content 帖子正文
 *   @param {Array} attachments 附件信息
 *   @param {Array} ext 第三方附件信息
 * @param {Object} config 配置
 *   @param {Number} imgMaxWidth 图片限宽
 *   @param {Boolean} viewType 正文展示类型, thread(帖子) | editor(编辑器)
 *   @param {Boolean} cloudTransFer 是否需要转码
 */
export function richText4View (threadDetail, config) {
  const systemStore = useSystemStore()
  
  const { type, content = '', attachments = [], ext = [] } = threadDetail || {}
  const { imgMaxWidth = 792, viewType = 'thread', cloudTransFer = true } = config || {}

  let formatContent = xss(content, viewType === 'editor' ? editorXssWhiteList : threadXssWhiteList)

  // 内容图片处理
  if (viewType === 'comment') {
    formatContent = removeImg(content)
  } else if (viewType === 'quote') {
    formatContent = replaceQuoteImg(content)
  } else {
    // 格式化视频标签
    formatContent = formatVideo(formatContent, attachments, viewType)
    // 格式化图片标签
    formatContent = formatImg(formatContent, attachments, imgMaxWidth, viewType, cloudTransFer)
  }
  // @人处理
  formatContent = formatAtTag(formatContent)

  // pc端短文帖子展示（非编辑器）展示，媒体文件紧跟在文本后
  if (type === 2 && (viewType === 'thread')) {
    formatContent = formatMomentImg(formatContent, attachments, ext,  imgMaxWidth, cloudTransFer)
  }
  // 移动端字体大小转换
  try {
    if (!systemStore.isWeb) {
      formatContent = formatContent.replaceAll('14px', '12px')
      formatContent = formatContent.replaceAll('18px', '14px')
      formatContent = formatContent.replaceAll('24px', '20px')
      formatContent = formatContent.replaceAll('30px', '24px')
    }
    // 移除换行符
    formatContent = formatContent.replaceAll(/\n/g, '').replaceAll('<p></p>', '<br />')
  } catch {}
  return formatContent
}

// 格式化上传图片标签
function formatUploadImg (content, formatType) {
  const regex = new RegExp(`<([a-z]+)([^>]*\\bmedia-preview\\b[^>]*)>(.*?)</\\1>`, 'gi');
  let imgAttachments = []
  let formatImgTag = JSON.parse(JSON.stringify(content))
  const previewDomList = formatImgTag.match(regex) || []
  previewDomList.forEach(dom => {
    let img = dom.match(/<img\b[^>]*\bdata-value\b[^>]*>/gi)
    // 匹配上则重组，无则置空
    if (img) {
      img = img[0]
      // 图片id/链接
      const dataValue = getRegex({
        tag: 'img', 
        key: 'data-value', 
        regString: img
      })
      // 是否缩放
      const dataReduce = getRegex({
        tag: 'img', 
        key: 'data-reduce', 
        regString: img
      })
      const isReduce = dataReduce === 'true'
      // 图片类型
      const dataType = getRegex({
        tag: 'img', 
        key: 'data-type', 
        regString: img
      })
      const dataSrc = getRegex({
        tag: 'img', 
        key: 'src', 
        regString: img
      })
      const width = getRegex({
        tag: 'img', 
        key: 'width', 
        regString: img
      })
      const height = getRegex({
        tag: 'img', 
        key: 'height', 
        regString: img
      })
      // 重组
      const imgTag = `<img data-type="${dataType}" data-value="${dataValue}" data-reduce="${isReduce}" />`
      formatImgTag = formatImgTag.replace(dom, imgTag)
      let attach = {
        type: 1,
        id: dataValue
      }
      if (formatType === 'preview' || formatType === 'localData') {
        let imgBlobUrl = dataSrc
        try {
          imgBlobUrl = getBase64URL(dataSrc)
        } catch {}
        Object.assign(attach, {
          url: imgBlobUrl,
          width,
          height
        })
      }
      imgAttachments.push(attach)
    }
  })
  if (formatType === 'quote') {
    const quoteRegex = new RegExp(`<([a-z]+)([^>]*\\bdata-imgId\\b[^>]*)>(.*?)</\\1>`, 'gi');
    let quoteImgs = formatImgTag.match(quoteRegex) || []
    quoteImgs.forEach(div => {
      if (div) {
        // 图片id/链接
        const dataValue = getRegex({
          tag: 'div', 
          key: 'data-imgId', 
          regString: div
        })
        // 重组
        const imgTag = `<img data-type="file" data-value="${dataValue}" />`
        imgAttachments.push(dataValue)
        formatImgTag = formatImgTag.replace(div, imgTag)
      }
    })
  }
  const emojis = formatImgTag.match(/<img\b[^>]*\bdata-type="sticker_self"[^>]*>/gi) || []
  emojis.forEach(emoji => {
    // emoji id
    const dataValue = getRegex({
      tag: 'img', 
      key: 'data-value', 
      regString: emoji
    })
    const dataType = getRegex({
      tag: 'img', 
      key: 'data-type', 
      regString: emoji
    })
    const dataSrc = getRegex({
      tag: 'img', 
      key: 'src', 
      regString: emoji
    })
    const emojiTag = `<img data-type="${dataType}" ${(formatType === 'preview' || formatType === 'localData') ? `src="${dataSrc}"` : ''} data-value="${dataValue}"/>`

    formatImgTag = formatImgTag.replace(emoji, emojiTag)
  })
  return {
    imgAttachments,
    formatImgTag
  }
}

// 格式化上传视频标签
function formatUploadVideo (content, formatType) {
  const regex = new RegExp(`<([a-z]+)([^>]*\\bmedia-preview\\b[^>]*)>(.*?)</\\1>`, 'gi');
  let videoAttachments = []
  let youtubeAttatchments = []
  let formatVideoTag = JSON.parse(JSON.stringify(content))
  const previewDomList = formatVideoTag.match(regex) || []
  // 视频信息提取&生成替换标签
  const formatTag = (mediaTag, dom, tag) => {
    let videoTag = ''
    if (mediaTag) {
      mediaTag = mediaTag[0]
      const dataType = getRegex({
        tag: tag, 
        key: 'data-type', 
        regString: mediaTag
      })
      const dataValue = getRegex({
        tag: tag, 
        key: 'data-value', 
        regString: mediaTag
      })
      const dataSrc = getRegex({
        tag: tag, 
        key: 'src', 
        regString: mediaTag
      })
      const width = getRegex({
        tag: 'video', 
        key: 'width', 
        regString: mediaTag
      })
      const height = getRegex({
        tag: 'video', 
        key: 'height', 
        regString: mediaTag
      })
      videoTag = `<video data-type="${dataType}" data-value="${dataValue}"></video>`
      switch (dataType) {
        case 'file':
          let attach = {
            type: 2,
            id: dataValue
          }
          if (formatType === 'preview' || formatType === 'localData') {
            Object.assign(attach, {
              url: dataSrc,
              width,
              height
            })
          }
          videoAttachments.push(attach)
          break
        case 'youtube':
          youtubeAttatchments.push({
            url: dataValue,
            type: 'video',
            source: dataType
          })
          break
        default:
      }
    }
    // 匹配上则重组，无则置空
    formatVideoTag = formatVideoTag.replace(dom, videoTag)
  }
  // 遍历标签
  previewDomList.forEach(dom => {
    let video = dom.match(/<video.*?<\/video>/gi)
    let iframe = dom.match(/<iframe.*?<\/iframe>/gi)
    formatTag(iframe || video, dom, video ? 'video' : 'iframe')
  })
  return {
    youtubeAttatchments,
    videoAttachments,
    formatVideoTag
  }
}

// 格式化链接标签
function formatUploadLink (content) {
  let formatLinkTag = JSON.parse(JSON.stringify(content))
  const tagList = formatLinkTag.match(/<a.*?<\/a>/gi) || []

  tagList.forEach(tag => {
    let filtXssValue = tag.replace(/&lt;/g, "").replace(/&gt;/g, "")
    const dataType = getRegex({
      tag: 'a', 
      key: 'data-type', 
      regString: filtXssValue
    })
    const dataValue = getRegex({
      tag: 'a', 
      key: 'data-value', 
      regString: filtXssValue
    })
    const tagText = getRegex({
      reg: /<a[^>]*>(.*?)<\/a>/gi,
      regString: filtXssValue
    })

    const linkTag = `<a data-type="${dataType}" data-value="${dataValue}">${tagText}</a>`
    formatLinkTag = formatLinkTag.replace(tag, linkTag)
  })
  // 全局遍历链接，替换为a标签
  try {
    formatLinkTag = formatLinkTag.replaceAll(/(((https?:(?:\/\/)?)(?:[-;:&=\+\$,\w]+@)?[A-Za-z0-9.-])((?:\/[\+~%\/.\w-_]*)?\??(?:[-\+=&;%@.\w_]*)#?(?:[\w]*))?)[-A-Za-z0-9+&@#/%?=~_|!:,.;]+[-A-Za-z0-9+&@#/%=~_|](?!([^<]*>)|([^<]*<\/a>))/gi, (e) => {
      return `<a data-type="link" data-value="${e}">${e}</a>`
    })
  } catch(e) {
    console.log(e)
  }
  return formatLinkTag
}

/** 
 * @description: 富文本上传前处理
 * @param {String} content 帖子正文
 * @param {Object} config 配置
 *   @param {Boolean} formatType 格式化类型 thread(帖子) | 
 */
export function richText4Upload (originContent, config = {}) {
  const systemStore = useSystemStore()
  const { formatType = 'editor' } = config

  let formatUploadContent = JSON.parse(JSON.stringify(originContent)) || ''

  // 替换空白行为换行标签，移除换行符
  formatUploadContent = formatUploadContent.replaceAll('\n', '').replaceAll('<p>&nbsp;</p>', '<br />')
  // 移动端字号改变
  try {
    if (!systemStore.isWeb) {
      formatUploadContent = formatUploadContent.replaceAll('12px', '14px')
      formatUploadContent = formatUploadContent.replaceAll('14px', '18px')
      formatUploadContent = formatUploadContent.replaceAll('20px', '24px')
      formatUploadContent = formatUploadContent.replaceAll('24px', '30px')
    }
  } catch {}

  let ext = []
  let attachments = []
  let hideExt = []
  let hideAttachments = []

  let outputData = {}

  // 隐藏内容标识元素
  const hiddenSymbol = formatUploadContent.match(/<div[^>]*data-hiddenline[^>]*>[\s\S]*?<\/div>/gi)?.[0]

  // 拆分可视文本于隐藏文本
  let [content, hideContent] = hiddenSymbol ? formatUploadContent.split(hiddenSymbol) : [formatUploadContent]
  // 格式化图片
  const { imgAttachments, formatImgTag } = formatUploadImg(content, formatType)
  content = formatImgTag
  // 格式化视频标签（包含本地视频，标签）
  const { youtubeAttatchments, videoAttachments, formatVideoTag } = formatUploadVideo(content, formatType)
  content = formatVideoTag
  // 格式化链接
  const formatLinkTag = formatUploadLink(content)
  content = formatLinkTag
  // 附件信息
  ext = [ ...youtubeAttatchments ]
  attachments = [ ...imgAttachments, ...videoAttachments ]
  // 输出
  Object.assign(outputData, {
    ext,
    content,
    attachments,
  })

  // 隐藏内容
  if (hideContent) {
    // 格式化图片
    const { 
      imgAttachments: hiddenImgAttachments,
      formatImgTag: hiddenFormatImgTag 
    } = formatUploadImg(hideContent, formatType)
    hideContent = hiddenFormatImgTag
    // 格式化视频标签（包含本地视频，标签）
    const { 
      youtubeAttatchments: hiddenYoutubeAttachments,
      videoAttachments: hiddenVideoAttachments,
      formatVideoTag: hiddenFormatVideoTag
    } = formatUploadVideo(hideContent, formatType)
    hideContent = hiddenFormatVideoTag
    // 格式化链接
    const hiddenFormatLinkTag = formatUploadLink(hideContent)
    hideContent = hiddenFormatLinkTag
    // 附件信息
    hideExt = [ ...hiddenYoutubeAttachments ]
    hideAttachments = [ ...hiddenImgAttachments, ...hiddenVideoAttachments ]
    // 输出
    Object.assign(outputData, {
      hideExt,
      hideContent,
      hideAttachments,
    })
  }
  return { ...outputData }

}

// 处理引用有序、无序列表截断问题
export const formatQuoteOrderList = (node) => {
	const dom = document.createElement('div')
	dom.innerHTML = node
	function setStart(node){
		if (node !== null) {
			let children = node.children || []
			//如果children.length存在
			for (let i = 0; i < children.length; i++) {
				//递归调用
				setStart(children[i])
				if(children[i].tagName === 'OL'){
					let index = children[i].firstChild.getAttribute('data-index')
					children[i].setAttribute('start', index)
				}
			}
		}
	}
	setStart(dom)
  return dom.innerHTML
}

export const recoverQuoteImg = (quote, attachments) => {
  let quoteContent = JSON.parse(JSON.stringify(quote))
  const quoteRegex = new RegExp(`<([a-z]+)([^>]*\\bdata-imgId\\b[^>]*)>(.*?)</\\1>`, 'gi');
  let quoteImgs = quoteContent.match(quoteRegex) || []
  quoteImgs.forEach(div => {
    if (div) {
      // 图片id/链接
      const dataValue = getRegex({
        tag: 'div', 
        key: 'data-imgId', 
        regString: div
      })
      const { url, oliveInfo, width, height } = attachments.find(i => i.id === dataValue) || {}
      // 重组
      let imgTag = `<img style="aspect-ratio: ${width}/${height}" src="${url}" />`
      if (oliveInfo) {
        imgTag = `<div data-type="olive"><img src="/photo/live.png" data-type="live_self" /><img style="aspect-ratio: ${width}/${height}" data-value="${dataValue}" data-src="${url}" /></div>`
      }
      quoteContent = quoteContent.replace(div, imgTag)
    }
  })
  return quoteContent
}

export const attributeRegExp = getRegex